/* eslint-disable no-await-in-loop */
import { Buffer } from 'buffer'
import JSZip from 'jszip'
import { createDeserializer, createServer, ServerClient, states } from 'minecraft-protocol'
import { proxy } from 'valtio'
import { LocalServer } from '../customServer'
import { UserError } from '../mineflayer/userError'

const FLASHBACK_MAGIC = -679_417_724
/** Native Paper/FlashBack 26.2 packet schema (protocol 776, data version 4903). */
export const FLASHBACK_PACKET_VERSION = '26.2'
export const FLASHBACK_CLIENT_VERSION = FLASHBACK_PACKET_VERSION

const actions = {
  nextTick: 'flashback:action/next_tick',
  gamePacket: 'flashback:action/game_packet',
  configurationPacket: 'flashback:action/configuration_packet',
  createLocalPlayer: 'flashback:action/create_local_player',
  moveEntities: 'flashback:action/move_entities',
  levelChunkCached: 'flashback:action/level_chunk_cached',
} as const

let activeReplayClient: ServerClient | undefined
let replayTeleportId = 1
const replayEntityPositions = new Map<number, { x: number, y: number, z: number, yaw: number, pitch: number }>()

type FlashbackMetadata = {
  uuid: string
  name: string
  world_name?: string
  version_string: string
  data_version: number
  protocol_version: number
  total_ticks: number
  chunks: Record<string, { duration: number, forcePlaySnapshot?: boolean }>
}

type FlashbackAction = {
  type: string
  payload: Uint8Array
  snapshot: boolean
}

export type FlashbackReplay = {
  metadata: FlashbackMetadata
  name: string
  sizeBytes: number
  actions: FlashbackAction[]
  cachedChunks: Uint8Array[]
}

export type FlashbackPlayer = {
  uuid: string
  username: string
  entityId?: number
}

export const flashbackReplayState = proxy({
  active: false,
  ready: false,
  isPlaying: false,
  currentTick: 0,
  totalTicks: 0,
  speed: 1,
  replayName: '',
  sourceVersion: '',
  skippedPackets: 0,
  playerMenuOpen: false,
  players: [] as FlashbackPlayer[],
})

class Cursor {
  position = 0

  constructor (readonly bytes: Uint8Array) {}

  get remaining () { return this.bytes.length - this.position }

  readByte () {
    if (this.remaining < 1) throw new UserError('Replay Flashback tronqué')
    return this.bytes[this.position++]!
  }

  readBoolean () { return this.readByte() !== 0 }

  readInt () {
    if (this.remaining < 4) throw new UserError('Replay Flashback tronqué')
    const view = new DataView(this.bytes.buffer, this.bytes.byteOffset + this.position, 4)
    const value = view.getInt32(0)
    this.position += 4
    return value
  }

  readFloat () {
    if (this.remaining < 4) throw new UserError('Replay Flashback tronqué')
    const view = new DataView(this.bytes.buffer, this.bytes.byteOffset + this.position, 4)
    const value = view.getFloat32(0)
    this.position += 4
    return value
  }

  readDouble () {
    if (this.remaining < 8) throw new UserError('Replay Flashback tronqué')
    const view = new DataView(this.bytes.buffer, this.bytes.byteOffset + this.position, 8)
    const value = view.getFloat64(0)
    this.position += 8
    return value
  }

  readVarInt () {
    let value = 0
    let shift = 0
    while (shift <= 35) {
      const current = this.readByte()
      value |= (current & 0x7f) << shift
      if ((current & 0x80) === 0) return value >>> 0
      shift += 7
    }
    throw new UserError('VarInt Flashback invalide')
  }

  readString () {
    const length = this.readVarInt()
    const value = new TextDecoder().decode(this.readBytes(length))
    return value
  }

  readBytes (length: number) {
    if (length < 0 || this.remaining < length) throw new UserError('Replay Flashback tronqué')
    const value = this.bytes.subarray(this.position, this.position + length)
    this.position += length
    return value
  }
}

const parseChunk = (bytes: Uint8Array): FlashbackAction[] => {
  const cursor = new Cursor(bytes)
  if (cursor.readInt() !== FLASHBACK_MAGIC) throw new UserError('Format de chunk Flashback inconnu')
  const actionCount = cursor.readVarInt()
  const actionNames = Array.from({ length: actionCount }, () => cursor.readString())
  const snapshotSize = cursor.readInt()
  const snapshotEnd = cursor.position + snapshotSize
  const parsed: FlashbackAction[] = []
  while (cursor.remaining > 0) {
    const type = actionNames[cursor.readVarInt()]
    const payloadLength = cursor.readInt()
    if (!type) throw new UserError('Action Flashback inconnue')
    parsed.push({ type, payload: cursor.readBytes(payloadLength), snapshot: cursor.position <= snapshotEnd })
  }
  return parsed
}

const readCachedChunks = async (zip: JSZip) => {
  const cacheFiles = Object.values(zip.files)
    .filter(file => !file.dir && /^level_chunk_caches\/\d+$/.test(file.name))
    .sort((a, b) => Number(a.name.split('/').at(-1)) - Number(b.name.split('/').at(-1)))
  const cachedChunks: Uint8Array[] = []
  for (const file of cacheFiles) {
    const cursor = new Cursor(await file.async('uint8array'))
    while (cursor.remaining > 0) cachedChunks.push(cursor.readBytes(cursor.readInt()))
  }
  return cachedChunks
}

export const parseFlashbackReplay = async (archive: ArrayBuffer, name = 'Replay Flashback'): Promise<FlashbackReplay> => {
  const zip = await JSZip.loadAsync(archive)
  const metadataFile = zip.file('metadata.json')
  if (!metadataFile) throw new UserError('metadata.json absent du replay Flashback')
  const metadata = JSON.parse(await metadataFile.async('text')) as FlashbackMetadata
  if (!metadata.chunks || !Number.isFinite(metadata.total_ticks)) throw new UserError('Métadonnées Flashback invalides')
  if (metadata.version_string !== FLASHBACK_PACKET_VERSION || metadata.protocol_version !== 776 || metadata.data_version !== 4903) {
    throw new UserError(`Replay Flashback ${metadata.version_string} non compatible : ce lecteur attend un replay natif 26.2`)
  }
  const parsedActions: FlashbackAction[] = []
  for (const chunkName of Object.keys(metadata.chunks)) {
    const chunk = zip.file(chunkName)
    if (!chunk) throw new UserError(`Chunk Flashback absent : ${chunkName}`)
    for (const action of parseChunk(await chunk.async('uint8array'))) parsedActions.push(action)
  }
  return {
    metadata,
    name,
    sizeBytes: archive.byteLength,
    actions: parsedActions,
    cachedChunks: await readCachedChunks(zip),
  }
}

const decodePacket = (decoder: any, payload: Uint8Array) => {
  const parsed = decoder.parsePacketBuffer(Buffer.from(payload))
  return parsed.data as { name: string, params: any }
}

const normalizeProtocolValue = (value: any): any => {
  if (typeof value === 'bigint') {
    return [Number(BigInt.asIntN(32, value >> 32n)), Number(BigInt.asIntN(32, value))]
  }
  if (Buffer.isBuffer(value) || value instanceof Uint8Array) return value
  if (Array.isArray(value)) return value.map(normalizeProtocolValue)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, normalizeProtocolValue(child)]))
  }
  return value
}

const waitUntilPlaying = async () => {
  while (!flashbackReplayState.isPlaying) await new Promise(resolve => { setTimeout(resolve, 50) })
}

const tickDelay = async () => {
  await new Promise(resolve => { setTimeout(resolve, 50 / Math.max(0.25, flashbackReplayState.speed)) })
}

const angleByte = (degrees: number) => Math.round(degrees * 256 / 360)

const playMovementBatch = (client: ServerClient, payload: Uint8Array) => {
  const cursor = new Cursor(payload)
  const formatVersion = cursor.readVarInt()
  if (formatVersion !== 1) return
  cursor.readString() // dimension identifier; the V1 player only renders the active replay dimension.
  const count = cursor.readVarInt()
  for (let index = 0; index < count; index++) {
    const entityId = cursor.readVarInt()
    const x = cursor.readDouble()
    const y = cursor.readDouble()
    const z = cursor.readDouble()
    const yaw = cursor.readFloat()
    const pitch = cursor.readFloat()
    cursor.readFloat() // head yaw is already carried by native head-rotation packets.
    const onGround = cursor.readBoolean()
    replayEntityPositions.set(entityId, { x, y, z, yaw, pitch })
    client.write('entity_teleport', { entityId, x, y, z, yaw: angleByte(yaw), pitch: angleByte(pitch), onGround })
  }
}

const localPlayerPosition = (payload: Uint8Array) => {
  const cursor = new Cursor(payload)
  cursor.readBytes(16)
  return {
    x: cursor.readDouble(),
    y: cursor.readDouble(),
    z: cursor.readDouble(),
    pitch: cursor.readFloat(),
    yaw: cursor.readFloat(),
  }
}

const runFlashbackReplay = async (client: ServerClient, replay: FlashbackReplay, targetTick: number) => {
  activeReplayClient = client
  const playDecoder = createDeserializer({ state: states.PLAY, isServer: false, version: FLASHBACK_PACKET_VERSION, noErrorLogging: true } as any)
  let initialPosition: ReturnType<typeof localPlayerPosition> | undefined
  let snapshotChunkCount = 0
  let snapshotBatchStarted = false
  let loginJustSent = false
  const scoreboardObjectives = new Set<string>()

  const rememberReplayPlayers = (packet: { name: string, params: any }) => {
    switch (packet.name) {
      case 'player_info': {
        for (const entry of packet.params.data ?? []) {
          if (!entry.player?.name) continue
          const previous = flashbackReplayState.players.find(player => player.uuid === entry.uuid)
          if (previous) previous.username = entry.player.name
          else flashbackReplayState.players.push({ uuid: entry.uuid, username: entry.player.name })
        }

        break
      }
      case 'spawn_entity': {
        const player = flashbackReplayState.players.find(entry => entry.uuid === packet.params.objectUUID)
        if (player) {
          player.entityId = packet.params.entityId
          replayEntityPositions.set(packet.params.entityId, {
            x: packet.params.x,
            y: packet.params.y,
            z: packet.params.z,
            yaw: packet.params.yaw,
            pitch: packet.params.pitch,
          })
        }

        break
      }
      case 'player_remove': {
        const removed = new Set<string>(packet.params.players ?? [])
        flashbackReplayState.players = flashbackReplayState.players.filter(player => !removed.has(player.uuid))

        break
      }
    // No default
    }
  }

  const ensureScoreboardObjective = (packet: { name: string, params: any }) => {
    if (packet.name === 'scoreboard_objective') {
      if (packet.params.action === 1) { scoreboardObjectives.delete(packet.params.name) } else if (packet.params.action === 0) { scoreboardObjectives.add(packet.params.name) } else if (packet.params.action === 2 && !scoreboardObjectives.has(packet.params.name)) {
        client.write(packet.name, { ...normalizeProtocolValue(packet.params), action: 0 })
        scoreboardObjectives.add(packet.params.name)
        return false
      }
    }
    return packet.name === 'scoreboard_score' && !scoreboardObjectives.has(packet.params.scoreName)
  }

  const playAction = (action: FlashbackAction) => {
    try {
      switch (action.type) {
        case actions.gamePacket: {
          const packet = decodePacket(playDecoder, action.payload)
          rememberReplayPlayers(packet)
          if (ensureScoreboardObjective(packet)) return
          const params = normalizeProtocolValue(packet.params)
          if (packet.name === 'map_chunk') {
            if (action.snapshot && !snapshotBatchStarted) {
              client.write('chunk_batch_start', {})
              snapshotBatchStarted = true
            }
            if (action.snapshot) snapshotChunkCount++
          }
          client.write(packet.name, params)
          if (packet.name === 'login') loginJustSent = true

          break
        }
        case actions.levelChunkCached: {
          const cacheIndex = new Cursor(action.payload).readVarInt()
          const cached = replay.cachedChunks[cacheIndex]
          if (!cached) throw new UserError(`Chunk en cache Flashback absent : ${cacheIndex}`)
          const packet = decodePacket(playDecoder, cached)
          const params = normalizeProtocolValue(packet.params)
          if (packet.name === 'map_chunk') {
            if (action.snapshot && !snapshotBatchStarted) {
              client.write('chunk_batch_start', {})
              snapshotBatchStarted = true
            }
            if (action.snapshot) snapshotChunkCount++
          }
          client.write(packet.name, params)

          break
        }
        case actions.moveEntities: {
          playMovementBatch(client, action.payload)

          break
        }
        case actions.createLocalPlayer: {
          initialPosition = localPlayerPosition(action.payload)

          break
        }
      // No default
      }
    } catch (error) {
      console.warn('Paquet Flashback ignoré', error)
      flashbackReplayState.skippedPackets++
    }
  }

  for (const action of replay.actions) {
    if (action.snapshot) {
      playAction(action)
      // The replay snapshot is synchronous, but the viewer initializes its
      // world listeners from the login event. Give those listeners a turn
      // before replaying the initial chunk batch.
      if (loginJustSent) {
        loginJustSent = false
        await new Promise(resolve => { setTimeout(resolve, 250) })
      }
    }
  }
  if (snapshotBatchStarted) client.write('chunk_batch_finished', { batchSize: snapshotChunkCount })

  if (initialPosition) {
    client.write('update_view_position', {
      chunkX: Math.floor(initialPosition.x / 16),
      chunkZ: Math.floor(initialPosition.z / 16),
    })
    client.write('update_view_distance', { viewDistance: 2 })
    client.write('position', {
      teleportId: 0,
      ...initialPosition,
      dx: 0,
      dy: 0,
      dz: 0,
      flags: 0,
    })
  }
  client.write('game_state_change', { reason: 3, gameMode: 3 })
  client.write('abilities', { flags: 6, flyingSpeed: 0.05, walkingSpeed: 0.1 })
  flashbackReplayState.ready = true

  let targetReached = false
  for (const action of replay.actions) {
    if (action.snapshot) continue
    if (action.type === actions.nextTick) {
      flashbackReplayState.currentTick++
      if (flashbackReplayState.currentTick >= targetTick) {
        if (!targetReached) {
          targetReached = true
          if (targetTick > 0) flashbackReplayState.isPlaying = false
        }
        await waitUntilPlaying()
        await tickDelay()
      }
      continue
    }
    playAction(action)
  }
  flashbackReplayState.isPlaying = false
}

export const startFlashbackReplayServer = (replay: FlashbackReplay, targetTick = 0) => {
  activeReplayClient = undefined
  replayEntityPositions.clear()
  replayTeleportId = 1
  Object.assign(flashbackReplayState, {
    active: true,
    ready: false,
    isPlaying: false,
    currentTick: 0,
    totalTicks: replay.metadata.total_ticks,
    speed: 1,
    replayName: replay.name,
    sourceVersion: replay.metadata.version_string,
    skippedPackets: 0,
    playerMenuOpen: false,
    players: [],
  })

  const server = createServer({
    Server: LocalServer as any,
    version: FLASHBACK_CLIENT_VERSION,
    keepAlive: false,
    'online-mode': false,
  })
  server.on('playerJoin', client => {
    void runFlashbackReplay(client, replay, Math.min(Math.max(0, targetTick), replay.metadata.total_ticks))
  })
  return { server, version: FLASHBACK_CLIENT_VERSION }
}

export const teleportToReplayPlayer = (entityId: number) => {
  const position = replayEntityPositions.get(entityId)
  if (!activeReplayClient || !position) return false
  activeReplayClient.write('position', {
    teleportId: replayTeleportId++,
    ...position,
    dx: 0,
    dy: 0,
    dz: 0,
    flags: 0,
  })
  flashbackReplayState.playerMenuOpen = false
  return true
}

export const seekFlashbackReplay = (tick: number) => {
  const url = new URL(window.location.href)
  url.searchParams.set('replayTick', String(Math.round(tick)))
  window.location.assign(url)
}
